/**
 * Demand Note installment rules as the pages need them. Pure functions only,
 * so a page can tell the applicant what is wrong before anything is sent. The
 * server applies the same rules again in
 * api/payment-installments/installmentRules.js; keep the two in step.
 */

/**
 * Where one application stands under the installment rules, as the server
 * reports it. When `applies` is false the rules do not govern the application
 * and the money fields are absent.
 */
export type TInstallmentContext = {
  applies: boolean;
  applicationId: number;
  returnCount: number;
  maxReturnsAllowed: number;
  canReturn: boolean;
  totalAmount?: number;
  paidAmount?: number;
  remainingBalance?: number;
  activeInstallmentCount?: number;
  maxInstallmentsAllowed?: number;
};

/** An installment context whose rules are in force, with the money fields present. */
export type TActiveInstallmentContext = Required<TInstallmentContext>;

/** Amounts closer than this are treated as equal, to absorb decimal rounding. */
const AMOUNT_EPSILON = 0.01;

/** Narrows a context to one whose rules are in force. */
export function installmentsApply(
  ctx: TInstallmentContext | null | undefined
): ctx is TActiveInstallmentContext {
  return Boolean(ctx?.applies);
}

/** How many more receipts the applicant may still add in this attempt. */
export function slotsLeft(ctx: TActiveInstallmentContext) {
  return Math.max(ctx.maxInstallmentsAllowed - ctx.activeInstallmentCount, 0);
}

/** True when the receipts of the current attempt add up to the whole amount. */
export function isFullyPaid(ctx: TActiveInstallmentContext) {
  return Math.abs(ctx.remainingBalance) <= AMOUNT_EPSILON;
}

/**
 * What is wrong with the amounts the applicant is about to save, in the order
 * entered, or null when they follow the rules.
 */
export function getNewAmountsError(
  ctx: TActiveInstallmentContext,
  amounts: number[]
): string | null {
  let runningActive = ctx.activeInstallmentCount;
  let runningRemaining = ctx.remainingBalance;

  for (const raw of amounts) {
    const amount = Number(raw);
    runningActive += 1;

    if (runningActive > ctx.maxInstallmentsAllowed) {
      return `Maximum ${ctx.maxInstallmentsAllowed} installment(s) allowed at this stage.`;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      return "Each installment needs an amount greater than zero.";
    }
    if (amount > runningRemaining + AMOUNT_EPSILON) {
      return `Amount cannot exceed the remaining balance of ${runningRemaining}.`;
    }
    const isFinalSlot = runningActive === ctx.maxInstallmentsAllowed;
    if (isFinalSlot && Math.abs(amount - runningRemaining) > AMOUNT_EPSILON) {
      return `This installment must exactly equal the remaining balance of ${runningRemaining}.`;
    }
    runningRemaining = Number((runningRemaining - amount).toFixed(2));
  }
  return null;
}
